import React, { useState, useEffect, useCallback } from 'react';

import { SquareMinus, SquarePlus } from 'lucide-react';

import DefaultTableResize from '../DefaultTableResize';
import { TruncateWithTooltipFormatter } from '../Grid/components/Table/formatters';

import { useRows, sorted, isLinkable } from './utils'

export default function HierarchicalTable({ columns: columnDefinition, data, onChange, comparison, onTogglePinColumn, onRemoveColumn, onReorderColumns, onOpenColumnsConfig }) {
  const rows = useRows([], onChange)
  const [columns, setColumns] = useState([{}])
  const [sortOptions, setSortOptions] = useState({ order: null })

  const setDefaultData = useCallback(() => {
    rows.set(data && data.children.map((_, i) => ({ ..._, level: 0, index: `${i}`, __rowKey: `${i}` })) || [])
  }, [data])

  useEffect(() => {
    setDefaultData()
  }, [data])

  useEffect(() => {
    setColumns(columnDefinition.map((colDef, idx) => idx === 0 ? expandFormat(colDef) : colDef))
  }, [columnDefinition.map(({ dataField, pinned }) => `${dataField}:${pinned}`).join(), columnDefinition[0]?.text])

  const handleSortColumn = (column, direction) => {
    const order = direction === 'asc' ? -1 : 1
    const isActive = sortOptions.dataField === column.dataField && sortOptions.order === order

    setSortOptions({
      dataField: isActive ? null : column.dataField,
      field: column.sortValue || column.dataField,
      order: isActive ? 0 : order,
    })
  }

  const sortState = sortOptions.order ? { dataField: sortOptions.dataField, direction: sortOptions.order === -1 ? 'asc' : 'desc' } : null

  const sort = array => {
    const { field, order } = sortOptions
    if (order) return sorted(array, typeof field === 'function' ? field : ({ [field]: f }) => f, order)
    return array
  }

  const hideChildren = (parent) => {
    if (!parent.expanded) return
    parent.children.forEach(child => {
      child.expanded && hideChildren(child)
      rows.remove(child)
    })
    parent.expanded = false
  }

  const showChildren = (parent, idx) => {
    const children = parent.children
    children.forEach((child, i) => {
      child.parent = parent
      child.index = `${parent.index}-${i}`
      child.__rowKey = `${parent.__rowKey}-${i}`
      child.level = parent.level + 1
    })
    rows.add(idx + 1, sort([...children]))
    parent.expanded = true
  }

  const handleExpandRow = (rowKey, rowIndex) => {
    const row = rows.find(r => r.__rowKey === rowKey)
    if (!row) return
    if (row.children) {
      if (row.expanded)
        hideChildren(row)
      else
        showChildren(row, rowIndex)
    }
  }

  useEffect(() => {
    if (sortOptions.order === 0) {
      setDefaultData()
      return
    }
    if (sortOptions.order) {
      rows.forEach(hideChildren)
      rows.set(sort(rows))
    }
  }, [sortOptions])

  const RowWrapper = ({ children, row, className }) => {
    const visibleIndex = typeof row?.index === 'number' ? row.index : undefined

    return <tr className={className} onClick={() => handleExpandRow(row?.__rowKey, visibleIndex)} >
      {children}
    </tr>
  }

  return <DefaultTableResize
    keyField='__rowKey'
    data={[...rows]}
    noDataIndication={() => 'No content to show.'}
    columns={columns}
    rowWrapper={RowWrapper}
    onTogglePinColumn={onTogglePinColumn}
    onRemoveColumn={onRemoveColumn}
    onReorderColumns={onReorderColumns}
    onSortColumn={handleSortColumn}
    sortState={sortState}
    onOpenColumnsConfig={onOpenColumnsConfig}
  />
}

const expandFormat = ({ formatter, ...definition }) => ({
  ...definition,
  formatter(_, row) {
    return <>
      <div
        className='flex items-center gap-1 twy-text-base-accent'
        style={{ paddingLeft: `${6 + row.level * 20}px` }}
      >
        {row.children && row.children.length ? <span className='cursor-pointer'>
          {row.expanded ? <SquareMinus size='16' /> : <SquarePlus size='16' />}
        </span> : null}
        <TruncateWithTooltipFormatter
          value={formatter(_, row)}
          className={isLinkable(row) ? 'twy-text-base-accent cursor-pointer hover:underline' : 'twy-text-base-accent'}
        />
      </div>
    </>
  },
})
